import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Store } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { useDirectoryListing } from './directory-api';

export function DirectoryListingCard({ workspaceId }: { workspaceId: string | null }) {
  const { t } = useTranslation('integrations');
  const navigate = useNavigate();
  const { data, isLoading } = useDirectoryListing(workspaceId);

  // Same three states the detail page renders, condensed into one badge:
  // granted (verified) beats pending beats not-claimed.
  const badge = data?.listing
    ? { label: t('directoryListing.claimedBadge'), className: 'bg-success/15 text-success' }
    : data?.pendingRequest
      ? { label: t('directoryListing.pendingBadge'), className: 'bg-warning/15 text-warning' }
      : { label: t('directoryListing.notClaimed'), className: 'bg-surface-2 text-muted' };

  return (
    <Card
      role="button"
      tabIndex={0}
      data-tour="integration-directory-listing"
      className="cursor-pointer transition-colors hover:border-accent focus-visible:border-accent focus-visible:outline-none"
      onClick={() => navigate('/integrations/directory-listing')}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          navigate('/integrations/directory-listing');
        }
      }}
    >
      <CardHeader>
        <span className="flex h-9 w-9 shrink-0 items-center justify-center border border-border bg-white">
          <Store size={20} className="text-accent" aria-hidden="true" />
        </span>
        <CardTitle>{t('directoryListing.title')}</CardTitle>
        <ChevronRight size={16} className="ml-auto text-muted" />
      </CardHeader>
      <CardContent className="flex items-center justify-between gap-4">
        <p className="text-xs text-muted">{t('directoryListing.cardDescription')}</p>
        {isLoading ? null : <span className={`shrink-0 px-2.5 py-1 text-xs font-bold uppercase ${badge.className}`}>{badge.label}</span>}
      </CardContent>
    </Card>
  );
}
